import { useConvertToModernPagination } from '@/context/services/hooks/usePagination';
import { isSuccess } from '@/context/services/type';
import { useResponseToInvoiceReportColumn } from '@/features/invoice_reports/components/List/hooks';
import type {
  FetchInvoiceReportsArgs,
  InvoiceReportColumn,
} from '@/features/invoice_reports/types';
import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import {
  CommonError,
  useGetInvoiceReportPaymentStatusesSuspense,
  useSearchInvoiceReports,
} from 'ap-openapi';
import { useMemo } from 'react';

const isCommonError = (value: unknown): value is CommonError =>
  typeof value === 'object' && value !== null;

const ForbiddenStatusCode = ['403', '401'];

export function useInvoiceReportsSearch(args: FetchInvoiceReportsArgs = {}) {
  const { page = 1, perPage = 50, query, order_by } = args;

  const { data, refetch, isFetching } = useSearchInvoiceReports(
    {
      pagination: {
        per_page: perPage,
        page,
      },
      query: query,
      order_by: order_by
        ? order_by.map((item) => ({
            field: item.field,
            direction: item.direction,
          }))
        : undefined,
    },
    {
      query: {
        queryKey: ['invoiceReports', { page, perPage, query, order_by }],
      },
    }
  );

  const pagination: ModernPaginationProps = useConvertToModernPagination(
    data?.data && typeof data.data === 'object' && 'pagination' in data.data
      ? data.data.pagination
      : undefined
  );

  const result: InvoiceReportColumn[] = useMemo(
    () =>
      data?.data &&
      typeof data.data === 'object' &&
      'invoice_reports' in data.data
        ? data.data.invoice_reports?.map(
            useResponseToInvoiceReportColumn<InvoiceReportColumn>
          ) ?? []
        : [],
    [data]
  );

  return {
    pagination,
    data: result,
    refetch,
    isFetching,
    isError:
      !isSuccess(data?.status) &&
      !ForbiddenStatusCode.includes(data?.status?.toString() || ''),
    error: data && isCommonError(data.data) ? { ...data.data } : {},
  };
}

export const useGetInvoiceReportsPaymentStatuses = () => {
  const { data, error, isLoading } = useGetInvoiceReportPaymentStatusesSuspense(
    {}
  );
  if (error) throw error;

  if (isSuccess(data?.status)) {
    return {
      data,
      isLoading,
    };
  } else {
    return {
      error: data && isCommonError(data.data) ? { ...data.data } : {},
      isLoading,
    };
  }
};
